import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Task } from '../page';

interface Comment {
  id: string;
  task_id: string;
  user_id: string | null;
  content: string;
  created_at: string;
  _user_name?: string;
  _is_own?: boolean;
  _pending?: boolean;
}

interface TaskViewModalProps {
  task: Task;
  userId: string;
  userName: string;
  onClose: () => void;
  onEdit?: () => void;
  canEdit?: boolean;
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  critical: { label: 'Crítica', color: 'text-red-700', icon: 'ri-arrow-up-double-line' },
  high: { label: 'Alta', color: 'text-red-500', icon: 'ri-arrow-up-line' },
  medium: { label: 'Média', color: 'text-orange-400', icon: 'ri-arrow-right-line' },
  low: { label: 'Baixa', color: 'text-slate-400', icon: 'ri-arrow-down-line' },
};

function initials(name: string) {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

function fmtTime(d: string) {
  const date = new Date(d);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return (
    date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' +
    date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

export default function TaskViewModal({ task, userId, userName, onClose, onEdit, canEdit }: TaskViewModalProps) {
  const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const attachments = (task as Task & { attachments?: { images?: string[] } }).attachments;
  const images = attachments?.images ?? [];

  // ── Chat state ──
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendError, setSendError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userNamesRef = useRef<Record<string, string>>({});

  const fetchUserNames = useCallback(async (ids: string[]) => {
    const missing = ids.filter(id => !userNamesRef.current[id]);
    if (!missing.length) return;
    const { data } = await supabase.from('profiles').select('id, full_name').in('id', missing);
    (data || []).forEach((u: { id: string; full_name: string }) => {
      userNamesRef.current[u.id] = u.full_name || 'Usuário';
    });
  }, []);

  const mapComment = useCallback((c: Comment): Comment => ({
    ...c,
    _user_name: c.user_id ? (userNamesRef.current[c.user_id] || 'Usuário') : 'Usuário',
    _is_own: c.user_id === userId,
  }), [userId]);

  const loadComments = useCallback(async () => {
    const { data } = await supabase
      .from('task_comments')
      .select('*')
      .eq('task_id', task.id)
      .order('created_at');
    const rows = data || [];
    const ids = [...new Set(rows.map((c: Comment) => c.user_id).filter(Boolean))] as string[];
    await fetchUserNames(ids);
    setComments(rows.map(mapComment));
    setLoadingComments(false);
  }, [task.id, fetchUserNames, mapComment]);

  useEffect(() => { loadComments(); }, [loadComments]);

  // Realtime
  useEffect(() => {
    const channel = supabase
      .channel(`view-comments-${task.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'task_comments', filter: `task_id=eq.${task.id}` },
        async (payload) => {
          const row = payload.new as Comment;
          if (row.user_id === userId) return;
          await fetchUserNames(row.user_id ? [row.user_id] : []);
          setComments(prev => prev.some(c => c.id === row.id) ? prev : [...prev, mapComment(row)]);
        })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'task_comments', filter: `task_id=eq.${task.id}` },
        (payload) => {
          setComments(prev => prev.filter(c => c.id !== (payload.old as Comment).id));
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [task.id, userId, fetchUserNames, mapComment]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!loadingComments) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [comments.length, loadingComments]);

  const sendNotifications = useCallback(async (text: string) => {
    try {
      const recipientSet = new Set<string>();

      // 1. Project members (includes admins assigned to this project)
      const { data: members } = await supabase.from('project_members').select('user_id').eq('project_id', task.project_id);
      (members || []).forEach((m: { user_id: string }) => { if (m.user_id !== userId) recipientSet.add(m.user_id); });

      // 2. All admin users (RLS now allows any authenticated user to see admin profiles)
      const { data: admins } = await supabase.from('profiles').select('id').eq('role', 'admin');
      (admins || []).forEach((a: { id: string }) => { if (a.id !== userId) recipientSet.add(a.id); });

      // 3. Client users linked to this project
      const { data: proj } = await supabase.from('projects').select('client_id').eq('id', task.project_id).maybeSingle();
      if (proj?.client_id) {
        const { data: cu } = await supabase.from('profiles').select('id').eq('client_id', proj.client_id);
        (cu || []).forEach((u: { id: string }) => { if (u.id !== userId) recipientSet.add(u.id); });
      }

      if (!recipientSet.size) return;
      const preview = text.length > 80 ? text.slice(0, 80) + '…' : text;
      await supabase.from('notifications').insert(
        Array.from(recipientSet).map(uid => ({
          user_id: uid, type: 'comment',
          title: `${userName} comentou em "${task.title}"`,
          body: preview, task_id: task.id, project_id: task.project_id,
          from_user_id: userId, read: false,
        }))
      );
    } catch (err) { console.error('Notif error:', err); }
  }, [task, userId, userName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text || saving) return;
    setSendError('');
    setSaving(true);
    const tempId = `temp-${Date.now()}`;
    const optimistic: Comment = {
      id: tempId, task_id: task.id, user_id: userId,
      content: text, created_at: new Date().toISOString(),
      _user_name: userName, _is_own: true, _pending: true,
    };
    setComments(prev => [...prev, optimistic]);
    setNewComment('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const { data, error } = await supabase
      .from('task_comments')
      .insert({ task_id: task.id, user_id: userId, content: text })
      .select().maybeSingle();

    if (error) {
      setComments(prev => prev.filter(c => c.id !== tempId));
      setNewComment(text);
      setSendError('Não foi possível enviar. Tente novamente.');
    } else if (data) {
      setComments(prev => prev.map(c => c.id === tempId ? { ...mapComment(data as Comment), _pending: false } : c));
      sendNotifications(text);
    }
    setSaving(false);
    textareaRef.current?.focus();
  };

  const handleDelete = async (id: string) => {
    setComments(prev => prev.filter(c => c.id !== id));
    setDeleteConfirm(null);
    await supabase.from('task_comments').delete().eq('id', id);
  };

  // Group consecutive messages
  const grouped = comments.map((c, i) => {
    const prev = comments[i - 1];
    const same = prev?.user_id === c.user_id;
    const diff = prev ? (new Date(c.created_at).getTime() - new Date(prev.created_at).getTime()) / 60000 : 999;
    return { comment: c, showMeta: !(same && diff < 5) };
  });

  const hasComments = comments.filter(c => !c._pending).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Wide modal: details left + chat right */}
      <div className={`bg-white rounded-xl w-full flex overflow-hidden ${hasComments ? 'max-w-4xl h-[88vh]' : 'max-w-lg max-h-[90vh] flex-col'}`}>

        {/* ── LEFT: Details ── */}
        <div className={`flex flex-col ${hasComments ? 'w-[45%] border-r border-slate-100 overflow-y-auto' : 'flex-1 overflow-y-auto'}`}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100">
                <i className="ri-eye-line text-slate-500 text-sm"></i>
              </div>
              <h2 className="text-slate-800 font-semibold text-sm">Detalhes do Item</h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="px-5 py-5 space-y-4 flex-1">
            {/* Title */}
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Título</p>
              <p className="text-slate-800 font-semibold text-base leading-snug">{task.title}</p>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'}`}>
                <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                {task.type === 'bug' ? 'Bug / Correção' : 'Nova Demanda'}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 ${prio.color}`}>
                <i className={prio.icon}></i>
                {prio.label}
              </span>
              {task.sprint_desired && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
                  <i className="ri-star-fill"></i>Próxima sprint
                </span>
              )}
            </div>

            {/* Description */}
            {task.description && (
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">Descrição</p>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                  {task.description}
                </p>
              </div>
            )}

            {/* Observations */}
            {task.observations && (
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">Observações</p>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                  {task.observations}
                </p>
              </div>
            )}

            {/* Image */}
            {images.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Imagem anexada</p>
                <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <img src={images[0]} alt="Imagem anexada" className="w-full max-h-52 object-contain" />
                </div>
                <a href={images[0]} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-xs text-cyan-600 hover:underline cursor-pointer">
                  <i className="ri-external-link-line"></i>Abrir em tamanho completo
                </a>
              </div>
            )}

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-2">
              {(task.estimate_hours > 0 || task.estimate_days > 0) && (
                <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                  <p className="text-xs text-slate-400 mb-0.5">Estimativa</p>
                  <p className="text-slate-700 text-sm font-medium">
                    {task.estimate_hours > 0 ? `${task.estimate_hours}h` : ''}
                    {task.estimate_hours > 0 && task.estimate_days > 0 ? ' / ' : ''}
                    {task.estimate_days > 0 ? `${task.estimate_days}d` : ''}
                  </p>
                </div>
              )}
              {task.value > 0 && (
                <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                  <p className="text-xs text-slate-400 mb-0.5">Valor</p>
                  <div className="flex items-center gap-1">
                    <p className="text-slate-700 text-sm font-medium">
                      {task.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                    {task.paid && <i className="ri-checkbox-circle-fill text-emerald-500 text-xs"></i>}
                  </div>
                </div>
              )}
              <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                <p className="text-xs text-slate-400 mb-0.5">Criado em</p>
                <p className="text-slate-700 text-sm font-medium">{new Date(task.created_at).toLocaleDateString('pt-BR')}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
            {canEdit && onEdit && (
              <button onClick={onEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-edit-line text-sm"></i>Editar
              </button>
            )}
            <button onClick={onClose}
              className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Fechar
            </button>
          </div>
        </div>

        {/* ── RIGHT: Chat (only when there are comments) ── */}
        {hasComments && (
          <div className="flex-1 flex flex-col min-w-0">
            {/* Chat header */}
            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 shrink-0">
              <div className="w-7 h-7 flex items-center justify-center rounded-xl bg-cyan-50">
                <i className="ri-chat-3-line text-cyan-500 text-sm"></i>
              </div>
              <span className="text-slate-700 font-semibold text-sm">Comentários</span>
              <span className="ml-auto px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-bold">
                {comments.filter(c => !c._pending).length}
              </span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 bg-slate-50/50 space-y-0.5">
              {loadingComments ? (
                <div className="flex items-center justify-center h-full">
                  <i className="ri-loader-4-line animate-spin text-cyan-500 text-xl"></i>
                </div>
              ) : (
                <>
                  {grouped.map(({ comment: c, showMeta }) => {
                    const isOwn = c._is_own;
                    return (
                      <div key={c.id} className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'} ${showMeta ? 'mt-3' : 'mt-0.5'}`}>
                        <div className={`w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${showMeta ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${isOwn ? 'bg-cyan-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                          {initials(c._user_name || 'U')}
                        </div>
                        <div className={`group flex flex-col max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
                          {showMeta && !isOwn && (
                            <span className="text-xs text-slate-400 font-medium mb-0.5 ml-1">{c._user_name}</span>
                          )}
                          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${isOwn ? `bg-cyan-500 text-white rounded-br-sm ${c._pending ? 'opacity-70' : ''}` : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm'}`}>
                            <p className="whitespace-pre-wrap break-words">{c.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-0.5 ${isOwn ? 'text-cyan-100' : 'text-slate-400'}`}>
                              <span className="text-xs">{fmtTime(c.created_at)}</span>
                              {isOwn && (c._pending ? <i className="ri-time-line text-xs opacity-70"></i> : <i className="ri-check-double-line text-xs"></i>)}
                            </div>
                          </div>
                          {isOwn && !c._pending && (
                            <button onClick={() => setDeleteConfirm(c.id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 text-xs text-slate-400 hover:text-red-500 cursor-pointer flex items-center gap-1">
                              <i className="ri-delete-bin-line text-xs"></i>Apagar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </>
              )}
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-slate-100 bg-white shrink-0">
              {sendError && (
                <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{sendError}
                </p>
              )}
              <form onSubmit={handleSubmit} className="flex items-end gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-cyan-500 text-white text-xs font-bold shrink-0 mb-1">
                  {initials(userName)}
                </div>
                <div className="flex-1">
                  <textarea
                    ref={textareaRef}
                    value={newComment}
                    onChange={e => { setNewComment(e.target.value); setSendError(''); }}
                    placeholder="Responder..."
                    rows={1}
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-2xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none bg-slate-50 focus:bg-white transition-colors"
                    style={{ minHeight: '38px', maxHeight: '100px' }}
                    onInput={e => {
                      const el = e.currentTarget;
                      el.style.height = 'auto';
                      el.style.height = Math.min(el.scrollHeight, 100) + 'px';
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                    }}
                  />
                </div>
                <button type="submit" disabled={saving || !newComment.trim()}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-200 disabled:text-slate-400 text-white transition-colors cursor-pointer shrink-0 mb-0.5">
                  {saving ? <i className="ri-loader-4-line animate-spin text-xs"></i> : <i className="ri-send-plane-fill text-xs"></i>}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Chat panel placeholder when no comments yet — show below details in single-column mode */}
        {!hasComments && (
          <div className="border-t border-slate-100 px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-xl bg-cyan-50">
                <i className="ri-chat-3-line text-cyan-500 text-sm"></i>
              </div>
              <span className="text-slate-700 font-semibold text-sm">Comentários</span>
            </div>
            {loadingComments ? (
              <div className="flex justify-center py-4">
                <i className="ri-loader-4-line animate-spin text-cyan-500 text-lg"></i>
              </div>
            ) : (
              <div className="flex flex-col items-center py-4 gap-2 text-slate-400">
                <i className="ri-chat-3-line text-2xl text-slate-300"></i>
                <p className="text-xs text-slate-400">Nenhum comentário ainda — seja o primeiro!</p>
              </div>
            )}
            {sendError && (
              <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>{sendError}
              </p>
            )}
            <form onSubmit={handleSubmit} className="flex items-end gap-2 mt-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-cyan-500 text-white text-xs font-bold shrink-0 mb-1">
                {initials(userName)}
              </div>
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={newComment}
                  onChange={e => { setNewComment(e.target.value); setSendError(''); }}
                  placeholder="Escreva um comentário..."
                  rows={1}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-2xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none bg-slate-50 focus:bg-white transition-colors"
                  style={{ minHeight: '38px', maxHeight: '100px' }}
                  onInput={e => {
                    const el = e.currentTarget;
                    el.style.height = 'auto';
                    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                  }}
                />
              </div>
              <button type="submit" disabled={saving || !newComment.trim()}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-200 disabled:text-slate-400 text-white transition-colors cursor-pointer shrink-0 mb-0.5">
                {saving ? <i className="ri-loader-4-line animate-spin text-xs"></i> : <i className="ri-send-plane-fill text-xs"></i>}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-xs">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-3">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h3 className="text-slate-800 font-semibold text-sm mb-1">Apagar mensagem?</h3>
            <p className="text-slate-500 text-xs mb-4">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-3 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap">Apagar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
